import { memo, useCallback } from "react";
import { useApplicationHandler } from "../hooks/use-application-handler";
import { openShareDialog } from "../utils/open-share-dialog";
import * as Styled from "./style";
import { useMobileActionButton } from "./use-mobile-action-button";
import { useRouter } from "next/router";
import { useDeleteBookmark } from "~/components/blocks/my/bookmark/my-bookmark-infinity-scroll/hooks/use-delete-bookmark";
import { isVideoLegacySeason } from "~/components/blocks/product/type-guard";
import { openDialog } from "~/components/stores/dialog/store";
import { openTextOnlyToast } from "~/components/stores/toast/store";
import { isTouchDisplay } from "~/const/device";
import { PATHS } from "~/const/paths";
import { useQueryString } from "~/hooks/use-query-string";
import { useRatingBlock } from "~/hooks/use-rating-block";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { G_VideoContentType } from "~/lib/graphql";
import { platform } from "~/lib/navigator/user-agent";
import { useAuth } from "~/provider/auth";
import { removeTrailingEndSlash } from "~/util/remove-trailing-end-slash";

type MobileActionButtonsProps = { videoData: G_GetVideoQuery["video"] };

/** 画面幅が「0px〜599px」の場合に表示するアクションボタンブロックコンポーネント */
export const MobileActionButtons = memo((props: MobileActionButtonsProps) => {
  const { videoData } = props;

  const router = useRouter();
  const { isLoggedIn, execAuth } = useAuth();
  const { season, content, spot } = useQueryString();
  const { isFavoriteRegistered, addBookmarkHandler, deleteBookmarkHandler } = useMobileActionButton({ videoData });

  const {
    applicationDmmTvInstall,
    applicationDmmTvScheme,
    application2DInstall,
    application2DScheme,
    applicationVrInstall,
    applicationVrScheme,
  } = useApplicationHandler({ videoData });

  // TODO: お気に入り一覧画面でお気に入りの登録解除の実現が厳しい為、一時的にお気に入りボタンを消している
  const isMyBookmark = router.pathname === removeTrailingEndSlash(PATHS.MY_BOOKMARK);

  const { isRestrictedContentByUserStatus } = useRatingBlock();

  const ratingCategory = videoData.rating.category;

  const { isDeletedById, deleteBookmark, restoreBookmark } = useDeleteBookmark();

  const handleFavoriteBtnPressed = useCallback(async () => {
    if (!isLoggedIn) {
      openDialog({
        title: "お気に入り登録は\nログイン後にできます",
        mainButtonName: "ログイン",
        mainButtonLabelText: "ログイン",
        description: "ログインしますか？",
        isDisabledAnimation: true,
        onMainButtonAction: execAuth,
      });

      return;
    }

    if (isFavoriteRegistered) {
      void deleteBookmarkHandler();
    } else {
      void addBookmarkHandler();
    }
  }, [isLoggedIn, isFavoriteRegistered]);

  const handlePlayWithApplicationBtnPressed = () => {
    if (isVideoLegacySeason(videoData)) {
      const {
        contentType,
        viewingRights: { iosURLSchemes, androidURLSchemes },
      } = videoData.content;

      const isVod2D = contentType === G_VideoContentType.Vod_2D;
      const isVodVr = contentType === G_VideoContentType.VodVr;

      const dialogTitle = isVod2D
        ? "グラビア動画をアプリで再生するには\nDMM動画プレイヤーが必要です"
        : "VR動画をアプリで再生するには\nDMM VR動画プレイヤーが必要です";

      const dialogDescription = isVod2D ? "アプリでの再生は\n「レンタル・購入」した作品のみ対応しています" : undefined;

      const isIosVr =
        isVodVr &&
        platform === "iOS" &&
        iosURLSchemes !== undefined &&
        iosURLSchemes !== null &&
        iosURLSchemes.length > 0;

      const isAndroidVr =
        isVodVr &&
        platform === "Android" &&
        androidURLSchemes !== undefined &&
        androidURLSchemes !== null &&
        androidURLSchemes.length > 0;

      openDialog({
        title: dialogTitle,
        description: dialogDescription,
        mainButtonName: "アプリをインストール",
        mainButtonLabelText: "アプリをインストール",
        onMainButtonAction: isVod2D ? application2DInstall : applicationVrInstall,
        isDisabledAnimation: true,
        subButtonName: isVod2D || isIosVr || isAndroidVr ? "アプリを起動" : undefined,
        subButtonLabelText: isVod2D || isIosVr || isAndroidVr ? "アプリを起動" : undefined,
        onSubButtonAction: isVod2D ? application2DScheme : applicationVrScheme,
        imageSrc: isVod2D ? "/images/appIcon_dmmplayer.png" : "/images/appIcon_dmmvrplayer.png",
      });

      return;
    }

    openDialog({
      title: "この動画をアプリで再生するには\nDMM TVアプリが必要です",
      mainButtonName: "アプリをインストール",
      mainButtonLabelText: "アプリをインストール",
      onMainButtonAction: applicationDmmTvInstall,
      isDisabledAnimation: true,
      subButtonName: "アプリを起動",
      subButtonLabelText: "アプリを起動",
      onSubButtonAction: () => applicationDmmTvScheme(season, content),
      imageSrc: "/images/appIcon_dmmtv.png",
    });
  };

  const isPendingRemoval = isDeletedById(videoData.id);

  const handleBookmarkActionOnBookmarkPage = () => {
    const action = isFavoriteRegistered && !isPendingRemoval ? "DELETE" : "ADD";
    action === "DELETE" ? deleteBookmark(videoData.id) : restoreBookmark(videoData.id);
    openTextOnlyToast(`${action}_BOOKMARK`);
  };

  return (
    <Styled.DivMobileRoot>
      {!isRestrictedContentByUserStatus(ratingCategory, true) && (
        <Styled.Button
          type="button"
          name="favorite"
          aria-label="お気に入り"
          onClick={isMyBookmark && isFavoriteRegistered ? handleBookmarkActionOnBookmarkPage : handleFavoriteBtnPressed}
        >
          <Styled.SpIcon name={isFavoriteRegistered && !isPendingRemoval ? "check" : "signPlus"} color="GRAY50" />

          <Styled.SpSpanLabel>お気に入り</Styled.SpSpanLabel>
        </Styled.Button>
      )}

      {isTouchDisplay && !isRestrictedContentByUserStatus(videoData.rating.category, true) && (
        <Styled.ButtonPlayWithApplication
          type="button"
          name="Play Button With Application"
          aria-label="アプリで再生ボタン"
          onClick={handlePlayWithApplicationBtnPressed}
        >
          <Styled.SpIcon name="blank" color="GRAY50" />
          <Styled.SpSpanLabel>アプリで再生</Styled.SpSpanLabel>
        </Styled.ButtonPlayWithApplication>
      )}

      <Styled.Button
        type="button"
        name="share"
        aria-label="URLシェアボタン"
        onClick={() => openShareDialog({ videoData, spot })}
      >
        <Styled.SpIcon name="share" color="GRAY50" />
        <Styled.SpSpanLabel>シェア</Styled.SpSpanLabel>
      </Styled.Button>
    </Styled.DivMobileRoot>
  );
});

MobileActionButtons.displayName = "MobileActionButtons";
